import {
  ColDef,
  ColGroupDef,
  GridOptions,
  ICellRendererParams,
  themeAlpine,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef } from "react";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import { IgTypography } from "@intelligems/ig-theme";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types";
import { DataType } from "@intelligems/ig-types";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { useSelector } from "@src/store";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  rowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerRowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerFontWeight: 600,
  // headerTextColor: "#6C6C6C",
  headerFontSize: "14px",
  wrapperBorder: "0px solid",
  wrapperBorderRadius: "8px",
  borderRadius: "8px",
});

const ConversionLoadingOverlay = (props: ICellRendererParams) => {
  return (
    <Stack direction={"column"}>
      <CircularProgress size={20} variant="indeterminate" />

      <IgTypography>Data loading... please wait</IgTypography>
    </Stack>
  );
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:not(:first-child):not(:last-child)`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    justifyContent: "center !important",
  },
  [`& .ag-header-cell-label`]: {
    justifyContent: "flex-end",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "16.8px",
  },
  [`& .ag-icon`]: {
    display: "none",
  },
  [`& .ag-group-child-count`]: {
    display: "none",
  },
  [`& .ag-cell:not(.ag-cell-alone)`]: {
    paddingLeft: "20px !important",
  },
  [`& .ag-cell.ag-cell-alone`]: {
    marginLeft: "0px !important",
    paddingLeft: "10px !important",
  },
  [`& .ag-row-group`]: {
    paddingLeft: "5px !important",
    fontWeight: "600 !important",
    backgroundColor: "rgb(245, 245, 245)!important",
  },
  [`& .ag-group-expanded`]: {
    marginRight: "0px !important",
  },
  [`& .ag-group-contracted`]: {
    marginRight: "0px !important",
  },
  [`& .ag-aria-description-container`]: {
    position: "absolute !important",
    top: "-9999px !important",
    left: "-9999px !important",
    height: "1px !important",
    width: "1px !important",
    overflow: "hidden !important",
  },
}));

interface ConversionFunnelTableProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  loading: boolean;
}

export const ConversionFunnelTable = ({
  variationOverviewData,
  loading,
}: ConversionFunnelTableProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const gridRef = useRef<AgGridReact>(null);
  const gridOptions = useMemo<GridOptions>(() => {
    return {
      tooltipInteraction: true,
      groupDisplayType: "groupRows",
      // groupRowRenderer: "agGroupCellRenderer",
      groupDefaultExpanded: -1,
      suppressAggFuncInHeader: true,
    };
  }, []);
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      resizable: false,
      cellStyle: {
        fontSize: "12px",
        alignContent: "center",
      },
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const tableData = useMemo(() => {
    if (variationOverviewData && variationOverviewData[0]?.data?.metrics) {
      return variationOverviewData[0].data.metrics.map((metricGroup) => {
        const checkoutAtcRate =
          (metricGroup["n_visitors_checkout_started"]?.value || 0) /
            (metricGroup["n_visitors_added_to_cart"]?.value || 0) || 0;
        const contactInfoRate =
          (metricGroup["n_visitors_checkout_contact_info_submitted"]?.value ||
            0) / (metricGroup["n_visitors_checkout_started"]?.value || 0) || 0;
        const addressInfoRate =
          (metricGroup["n_visitors_checkout_address_info_submitted"]?.value ||
            0) /
            (metricGroup["n_visitors_checkout_contact_info_submitted"]?.value ||
              0) || 0;
        const conversionRate =
          (metricGroup["n_visitors_ordered"]?.value || 0) /
            (metricGroup["n_visitors_checkout_address_info_submitted"]?.value ||
              0) || 0;

        return {
          ...metricGroup,
          // Store calculated metrics in the same format as API metrics
          n_checkout_atc_visitors: { value: checkoutAtcRate },
          n_checkout_address_info_submitted: { value: addressInfoRate },
          n_checkout_contact_info_submitted: { value: contactInfoRate },
          n_conversion_visitors: { value: conversionRate },
        };
      });
    } else
      return variations.map((variation) => ({
        value: 0,
        plus_minus: 0,
        p2bb: 0,
        p2bc: 0,
        ci_range: [0, 100],
        variation_id: variation.id,
      }));
  }, [variationOverviewData]);

  const getVariationMetricFormattedValue = useCallback(
    (variationId: string, metricId: string, metricDataType: DataType) => {
      const foundVariationData = tableData.find(
        (data) => data.variation_id === variationId
      );
      if (foundVariationData) {
        return graphValueFormatter({
          isAxisTick: false,
          value: foundVariationData[metricId]?.value || 0,
          configDataTypes: [metricDataType],
        });
      } else return "";
    },
    [tableData]
  );

  const rowData = useMemo(() => {
    return [
      {
        group: null,
        metric: "Visitors",
        shouldBeGrouped: false,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_visitors",
              "numberNoRounding"
            ),
          }),
          {}
        ),
      },
      {
        group: "Added to Cart",
        metric: "% Visitors",
        shouldBeGrouped: false,
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "add_to_cart_rate",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Began Checkout",
        metric: "% Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "checkout_begin_rate",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Began Checkout",
        metric: "% of ATC Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_checkout_atc_visitors",
              "percent"
            ),
          }),
          {}
        ),
        isProfit: true,
      },
      {
        group: "Contact Info Submitted",
        metric: "% Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "checkout_enter_contact_info_rate",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Contact Info Submitted",
        metric: "% of Checkout Started Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_checkout_contact_info_submitted",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Address Info Submitted",
        metric: "% Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "checkout_address_submitted_rate",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Address Info Submitted",
        metric: "% of Contact Submitted Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_checkout_address_info_submitted",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Converted",
        metric: "% Visitors",
        cellStyle: {
          marginLeft: "36px !important",
        },
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "conversion_rate",
              "percent"
            ),
          }),
          {}
        ),
      },
      {
        group: "Converted",
        metric: "% of Address Submitted Visitors",
        ...variations.reduce(
          (acc, variation) => ({
            ...acc,
            [variation.id]: getVariationMetricFormattedValue(
              variation.id,
              "n_conversion_visitors",
              "percent"
            ),
          }),
          {}
        ),
      },
    ];
  }, [tableData]);

  const columnDefs2: (ColDef | ColGroupDef)[] = useMemo(() => {
    const blankGroup: (ColDef | ColGroupDef)[] = [
      {
        headerName: "",
        field: "group",
        rowGroup: true,
        hide: true,
        cellRendererParams: {
          suppressCount: true,
        },
      },
      {
        headerName: "",
        field: "metric",
        width: 150,
        cellClassRules: {
          "ag-cell-alone": (params) =>
            params.data && params.data.group === null,
        },
        cellStyle: (params) => {
          const style = {
            fontWeight: 400,
            fontStyle: "normal",
            fontSize: "14px",
            backgroundColor: "white",
          };
          return style;
        },
        cellRendererParams: {
          suppressCount: true,
        },
      },
    ];

    const variationGroups: ColDef[] = variations.map((variation) => {
      return {
        headerName: variation.name,
        field: variation.id,
        cellClassRules: {
          "ag-cell-alone": (params) =>
            params.data && params.data.group === null,
        },
        cellStyle: (params) => {
          const style = {
            fontWeight: 400,
            fontStyle: "normal",
            textAlign: "right",
            fontSize: "14px",
            backgroundColor: "white",
          };
          return style;
        },
      };
    });

    return [...blankGroup, ...variationGroups];
  }, []);

  return (
    <StyledGrid size={"grow"} sx={{ marginTop: "0px", width: "100%" }}>
      <AgGridReact
        ref={gridRef}
        theme={myTheme}
        defaultColDef={defaultColDef}
        rowData={rowData}
        columnDefs={columnDefs2}
        gridOptions={gridOptions}
        suppressDragLeaveHidesColumns={true}
        suppressExcelExport={true}
        suppressCsvExport={true}
        suppressContextMenu={true}
        suppressCellFocus={true}
        loadingOverlayComponent={ConversionLoadingOverlay}
        groupHeaderHeight={41}
        headerHeight={41}
        // rowHeight={60}
        domLayout={"autoHeight"}
        // autoGroupColumnDef={autoGroupColumnDef}
        loading={loading}
        groupAllowUnbalanced={true}
      />
    </StyledGrid>
  );
};
